import { forwardRef, type ComponentRef } from "react";

import { Divider, Typography } from "../../atoms";
import { cn, cva } from "../../lib";
import { Box } from "../../primitives";
import {
  AccordionContentPrimitive,
  AccordionHeaderPrimitive,
  AccordionItemPrimitive,
  AccordionRootPrimitive,
  AccordionTriggerPrimitive,
  useAccordionTriggerDisabled,
} from "../../primitives/accordion";
import { AccordionChevron } from "./accordion-chevron";
import { AccordionContentInner } from "./accordion-content-inner";
import type {
  AccordionContentProps,
  AccordionItemProps,
  AccordionRootProps,
  AccordionTriggerProps,
} from "./types";

// The item's 16px padding and 8px label-to-body gap sit on the trigger and
// the content, so the trigger is a touch target: 16 + the 20px label + 8 =
// 44. With the item's 8 below it, a closed row is still 52. With a divider,
// the 8 sits above the Divider (Figma's: medium, inset 16), so the line is
// where the item's edge was
const accordionItemVariants = cva(
  "bg-luce-neutral-background-default flex w-full min-w-0 flex-col",
  {
    variants: {
      showDivider: {
        true: "",
        false: "pb-spacing-sm",
      },
    },
    defaultVariants: { showDivider: true },
  },
);

const Accordion = forwardRef<ComponentRef<typeof AccordionRootPrimitive>, AccordionRootProps>(
  ({ className, ...props }, ref) => (
    <AccordionRootPrimitive ref={ref} className={cn("w-full", className)} {...props} />
  ),
);

Accordion.displayName = "Accordion";

const AccordionItem = forwardRef<ComponentRef<typeof AccordionItemPrimitive>, AccordionItemProps>(
  ({ className, showDivider = true, children, ...props }, ref) => (
    <AccordionItemPrimitive
      ref={ref}
      className={cn(accordionItemVariants({ showDivider }), className)}
      {...props}
    >
      {children}
      {showDivider && (
        <Box className="pt-spacing-sm px-spacing-lg">
          <Divider />
        </Box>
      )}
    </AccordionItemPrimitive>
  ),
);

AccordionItem.displayName = "AccordionItem";

// An enabled trigger's feedback, as Card's and ListItem's (DQ3, the designer
// 2026-11-03): the fill of Figma's List Item `.State Background` helper on
// hover (pointers that hover, on web) and while pressed. Nothing fades
const triggerFeedbackClasses = [
  "web:[@media(hover:hover)]:hover:bg-luce-neutral-background-intermediate",
  "active:bg-luce-neutral-background-intermediate",
].join(" ");

/**
 * The item's header row. It renders its own heading, so don't wrap it in one.
 */
const AccordionTrigger = forwardRef<
  ComponentRef<typeof AccordionTriggerPrimitive>,
  AccordionTriggerProps
>(({ className, children, testID, ...props }, ref) => {
  // As the primitive resolves it (its own, the item's or the root's), so a
  // disabled row's colours match its behaviour
  const disabled = useAccordionTriggerDisabled(props.disabled);
  return (
    // A caller's className is the header's layout: the trigger inside keeps its
    // padding, states and focus ring
    <AccordionHeaderPrimitive className={className}>
      <AccordionTriggerPrimitive
        ref={ref}
        testID={testID}
        className={cn(
          "gap-spacing-sm px-spacing-lg pt-spacing-lg pb-spacing-sm group flex w-full flex-row items-center justify-between text-left outline-none",
          !disabled && triggerFeedbackClasses,
          // Disabled is a colour (DQ11), on the label and chevron below
          "disabled:pointer-events-none",
          // Keyboard focus on web: the ring Button and the selection controls
          // draw (`web:`, since css-interop drops `:focus-visible` on native),
          // inset: the trigger spans the row, and the body starts at its edge
          "web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus web:focus-visible:ring-inset",
        )}
        {...props}
      >
        {/* A label takes the row's free width, so a long one wraps beside the
          chevron (native text in a row doesn't shrink by itself) */}
        {typeof children === "string" || typeof children === "number" ? (
          <Typography
            variant="label-sm"
            color={disabled ? "neutral-foreground-attenuated" : "neutral-foreground-default"}
            className="min-w-0 flex-1"
          >
            {children}
          </Typography>
        ) : (
          <Box className="min-w-0 flex-1">{children}</Box>
        )}
        <AccordionChevron disabled={disabled} />
      </AccordionTriggerPrimitive>
    </AccordionHeaderPrimitive>
  );
});

AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = forwardRef<
  ComponentRef<typeof AccordionContentPrimitive>,
  AccordionContentProps
>(({ className, children, testID, forceMount, ...props }, ref) => (
  <AccordionContentPrimitive
    ref={ref}
    forceMount={forceMount}
    // A caller's className is the content's layout, on its outer element; the
    // inner wrapper keeps the padding. Forced content isn't animated: Radix
    // measures the open height while the content shows, and closed forced
    // content is hidden
    className={cn(
      !forceMount &&
        "web:transition-all web:data-[state=closed]:animate-accordion-up web:data-[state=open]:animate-accordion-down",
      "overflow-hidden",
      className,
    )}
    testID={testID}
    {...props}
  >
    <AccordionContentInner className="px-spacing-lg pb-spacing-sm">
      {typeof children === "string" || typeof children === "number" ? (
        // A block on web, so its own line height spaces its lines (an inline span
        // takes the line height around it)
        <Typography variant="body-sm" color="neutral-foreground-intermediate" className="web:block">
          {children}
        </Typography>
      ) : (
        children
      )}
    </AccordionContentInner>
  </AccordionContentPrimitive>
));

AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
